import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Search, X, Filter, Calendar, Users, RotateCcw, Dumbbell, UserPlus, Home, Clock } from 'lucide-react';
import WorkoutViewModal from './WorkoutViewModal';
import FullScreenWorkoutEditor from './FullScreenWorkoutEditor';
import GuidedTemplateBuilder from './GuidedTemplateBuilder';
import AssignFlexibleModal from './AssignFlexibleModal';
import BulkAssignTemplateModal from './BulkAssignTemplateModal';
import { estimateWorkoutSeconds, formatDuration } from '../../utils/workoutDuration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Items per page for pagination
const ITEMS_PER_PAGE = 20;

// Category color mapping for tags (matches Exercise Library)
const CATEGORY_COLORS = {
  'chest': { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-400/50', label: 'Chest' },
  'back': { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-400/50', label: 'Back' },
  'legs': { bg: 'bg-green-500/20', text: 'text-green-300', border: 'border-green-400/50', label: 'Legs' },
  'shoulders': { bg: 'bg-orange-500/20', text: 'text-orange-300', border: 'border-orange-400/50', label: 'Shoulders' },
  'arms': { bg: 'bg-purple-500/20', text: 'text-purple-300', border: 'border-purple-400/50', label: 'Arms' },
  'core': { bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-400/50', label: 'Core' },
  'cardio': { bg: 'bg-pink-500/20', text: 'text-pink-300', border: 'border-pink-400/50', label: 'Cardio' },
  'full-body': { bg: 'bg-gradient-to-r from-purple-500/20 to-blue-500/20', text: 'text-purple-200', border: 'border-purple-400/50', label: 'Full Body' },
  'stretching': { bg: 'bg-teal-500/20', text: 'text-teal-300', border: 'border-teal-400/50', label: 'Stretching' },
  'plyometrics': { bg: 'bg-amber-500/20', text: 'text-amber-300', border: 'border-amber-400/50', label: 'Plyometrics' },
  'neck': { bg: 'bg-slate-500/20', text: 'text-slate-300', border: 'border-slate-400/50', label: 'Neck' }
};

const getCategoryColor = (category) => {
  const normalized = category?.toLowerCase();
  return CATEGORY_COLORS[normalized] || { bg: 'bg-gray-500/20', text: 'text-gray-300', border: 'border-gray-400/50', label: category || 'Unknown' };
};

// Determine template's primary category from exercises
// Logic: majority category wins, if tied use first exercise's category, if evenly distributed use "full-body"
const getTemplateCategory = (templateExercises, exerciseLibrary) => {
  if (!templateExercises || templateExercises.length === 0) return null;
  
  // Count categories
  const categoryCount = {};
  let firstCategory = null;
  
  templateExercises.forEach((ex, idx) => {
    const libExercise = exerciseLibrary?.find(e => e.id === ex.exercise_id);
    const category = libExercise?.category || 'unknown';
    
    if (idx === 0) firstCategory = category;
    categoryCount[category] = (categoryCount[category] || 0) + 1;
  });
  
  // Find max count
  const entries = Object.entries(categoryCount);
  if (entries.length === 0) return null;
  
  const maxCount = Math.max(...entries.map(([_, count]) => count));
  const topCategories = entries.filter(([_, count]) => count === maxCount);
  
  // If only one category with max count → use it (majority)
  if (topCategories.length === 1) {
    return topCategories[0][0];
  }
  
  // If tied, check if evenly distributed (all categories have same count)
  const totalExercises = templateExercises.length;
  const uniqueCategories = entries.length;
  
  // Consider "evenly distributed" if we have 3+ categories and max appears less than 40% of total
  if (uniqueCategories >= 3 && (maxCount / totalExercises) < 0.4) {
    return 'full-body';
  }
  
  // Otherwise use first exercise's category (tie-breaker)
  return firstCategory;
};

// All categories for filter (matches Exercise Library)
const ALL_CATEGORIES = [
  { value: 'chest', label: 'Chest' },
  { value: 'back', label: 'Back' },
  { value: 'legs', label: 'Legs' },
  { value: 'shoulders', label: 'Shoulders' },
  { value: 'arms', label: 'Arms' },
  { value: 'core', label: 'Core' },
  { value: 'cardio', label: 'Cardio' },
  { value: 'full-body', label: 'Full Body' },
  { value: 'stretching', label: 'Stretching' },
  { value: 'plyometrics', label: 'Plyometrics' },
  { value: 'neck', label: 'Neck' }
];

// Muscle group color mapping for tags (secondary - used in expanded view)
const MUSCLE_GROUP_COLORS = {
  'Chest': { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-400/50' },
  'Back': { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-400/50' },
  'Lats': { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-400/50' },
  'Shoulders': { bg: 'bg-orange-500/20', text: 'text-orange-300', border: 'border-orange-400/50' },
  'Deltoids': { bg: 'bg-orange-500/20', text: 'text-orange-300', border: 'border-orange-400/50' },
  'Biceps': { bg: 'bg-purple-500/20', text: 'text-purple-300', border: 'border-purple-400/50' },
  'Triceps': { bg: 'bg-pink-500/20', text: 'text-pink-300', border: 'border-pink-400/50' },
  'Forearms': { bg: 'bg-indigo-500/20', text: 'text-indigo-300', border: 'border-indigo-400/50' },
  'Quadriceps': { bg: 'bg-green-500/20', text: 'text-green-300', border: 'border-green-400/50' },
  'Legs': { bg: 'bg-green-500/20', text: 'text-green-300', border: 'border-green-400/50' },
  'Hamstrings': { bg: 'bg-emerald-500/20', text: 'text-emerald-300', border: 'border-emerald-400/50' },
  'Glutes': { bg: 'bg-teal-500/20', text: 'text-teal-300', border: 'border-teal-400/50' },
  'Calves': { bg: 'bg-cyan-500/20', text: 'text-cyan-300', border: 'border-cyan-400/50' },
  'Abs': { bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-400/50' },
  'Core': { bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-400/50' },
  'Obliques': { bg: 'bg-amber-500/20', text: 'text-amber-300', border: 'border-amber-400/50' },
  'Trapezius': { bg: 'bg-slate-500/20', text: 'text-slate-300', border: 'border-slate-400/50' },
  'Rhomboids': { bg: 'bg-zinc-500/20', text: 'text-zinc-300', border: 'border-zinc-400/50' }
};

const getMuscleColor = (muscle) => {
  return MUSCLE_GROUP_COLORS[muscle] || { bg: 'bg-gray-500/20', text: 'text-gray-300', border: 'border-gray-400/50' };
};

// Format date as Fri 13/02/26
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2);
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
  return `${weekday} ${day}/${month}/${year}`;
};

const WorkoutTemplateLibrary = ({ exercises, onRefresh, t }) => {
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState([]);
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Expanded template tracking
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);
  
  // View executed workout modal
  const [viewingExecutedWorkout, setViewingExecutedWorkout] = useState(null);
  
  // Template metrics from backend
  const [templateMetrics, setTemplateMetrics] = useState({});
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recently_created');
  const [usageFilter, setUsageFilter] = useState('all');
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState('all'); // PRIMARY: Category filter for templates list
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  
  // Modals
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [usageDetailsModal, setUsageDetailsModal] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [futureBookings, setFutureBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  
  // Create Session Modal state
  const [createSessionModal, setCreateSessionModal] = useState(null);
  // Phase 2: Flexible workout assignment modal
  const [assignFlexibleModal, setAssignFlexibleModal] = useState(null);
  // P0-2: Bulk assignment modal
  const [bulkAssignModal, setBulkAssignModal] = useState(null);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  
  // Drag and drop state for exercise reordering
  const [draggedExercise, setDraggedExercise] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Guided Template Builder
  const [showGuidedBuilder, setShowGuidedBuilder] = useState(false);

  useEffect(() => {
    fetchAllWorkouts();
    fetchTemplateMetrics();
  }, []);

  const fetchTemplateMetrics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/trainer/template-execution-counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const metricsMap = {};
      (response.data.global_counts || []).forEach(item => {
        metricsMap[item.template_id] = {
          count: item.count,
          distinctClients: item.distinct_clients,
          lastUsed: item.last_used
        };
      });
      setTemplateMetrics(metricsMap);
    } catch (error) {
      console.error('Error fetching template metrics:', error);
    }
  };

  const fetchAllWorkouts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // FIX: Use client_id=template param to fetch from workout_templates collection
      const response = await axios.get(`${API}/workouts?client_id=template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Templates are returned directly, no need to filter
      setWorkouts(response.data || []);
    } catch (error) {
      console.error('Error fetching workouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFutureBookings = async () => {
    try {
      setLoadingBookings(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/trainer/future-bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFutureBookings(response.data);
    } catch (error) {
      console.error('Error fetching future bookings:', error);
    } finally {
      setLoadingBookings(false);
    }
  };

  const fetchUsageDetails = async (templateId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/trainer/template-usage-details/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsageDetailsModal(response.data);
    } catch (error) {
      console.error('Error fetching usage details:', error);
      alert('Error loading usage details');
    }
  };

  // Filter and sort templates
  const filteredTemplates = useMemo(() => {
    let result = [...workouts];
    
    // Search filter — matches name + tags + focus
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(w => {
        const name = (w.name || w.template_name || '').toLowerCase();
        const tags = (w.tags || []).map(t => t.toLowerCase());
        const focus = (w.focus || '').toLowerCase();
        return name.includes(search) || tags.some(t => t.includes(search)) || focus.includes(search);
      });
    }
    
    // Category filter (PRIMARY)
    if (templateCategoryFilter !== 'all') {
      result = result.filter(w => {
        const templateCategory = getTemplateCategory(w.exercises, exercises);
        return templateCategory === templateCategoryFilter;
      });
    }
    
    // Usage filter
    if (usageFilter !== 'all') {
      result = result.filter(w => {
        const metrics = templateMetrics[w.id];
        const count = metrics?.count || 0;
        if (usageFilter === 'unused') return count === 0;
        if (usageFilter === 'frequently_used') return count >= 5;
        return true;
      });
    }
    
    // Sort
    result.sort((a, b) => {
      const metricsA = templateMetrics[a.id] || {};
      const metricsB = templateMetrics[b.id] || {};
      
      switch (sortBy) {
        case 'most_used':
          return (metricsB.count || 0) - (metricsA.count || 0);
        case 'recently_used':
          const lastA = metricsA.lastUsed || '';
          const lastB = metricsB.lastUsed || '';
          return lastB.localeCompare(lastA);
        case 'name_asc':
          return (a.name || a.template_name || '').localeCompare(b.name || b.template_name || '');
        case 'recently_created':
        default:
          return (b.created_at || '').localeCompare(a.created_at || '');
      }
    });
    
    return result;
  }, [workouts, searchTerm, sortBy, usageFilter, templateCategoryFilter, templateMetrics, exercises]);

  // Pagination
  const totalPages = Math.ceil(filteredTemplates.length / ITEMS_PER_PAGE);
  const paginatedTemplates = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredTemplates.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTemplates, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setExpandedTemplateId(null);
  }, [searchTerm, sortBy, usageFilter, templateCategoryFilter]);

  // Handle delete
  const handleDeleteWorkout = async (workoutId, e) => {
    e?.stopPropagation();
    const metrics = templateMetrics[workoutId];
    const usageWarning = metrics?.count > 0 
      ? `\n\n⚠️ This template has been used ${metrics.count} times by ${metrics.distinctClients} client(s).`
      : '';
    
    if (window.confirm(`Are you sure you want to delete this template?${usageWarning}\n\nThis action cannot be undone.`)) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/workouts/${workoutId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchAllWorkouts();
        fetchTemplateMetrics();
      } catch (error) {
        console.error('Error deleting template:', error);
        alert('Error deleting template');
      }
    }
  };

  // Handle assign template to booking (legacy - kept for backwards compatibility)
  const handleAssignTemplate = async (bookingId) => {
    if (!assignModal) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/trainer/assign-template`, {
        booking_id: bookingId,
        template_id: assignModal.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Template assigned successfully!');
      setAssignModal(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error assigning template:', error);
      alert('Error assigning template: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Fetch clients for create session modal
  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  // Open create session modal
  const handleOpenCreateSession = (template, e) => {
    e?.stopPropagation();
    setCreateSessionModal(template);
    setClientSearchTerm('');
    fetchClients();
  };

  // Navigate to client planner with template pre-selected
  const handleCreateSessionForClient = (clientId) => {
    // Store template info in session storage for the planner to pick up
    sessionStorage.setItem('pendingTemplateForSession', JSON.stringify({
      templateId: createSessionModal.id,
      templateName: createSessionModal.name || createSessionModal.template_name
    }));
    setCreateSessionModal(null);
    // Navigate to client detail page with planner tab active
    navigate(`/admin/clients/${clientId}?tab=planner`);
  };

  // Filtered clients for search
  const filteredClients = useMemo(() => {
    if (!clientSearchTerm) return clients;
    const searchLower = clientSearchTerm.toLowerCase();
    return clients.filter(c => 
      c.full_name?.toLowerCase().includes(searchLower) ||
      c.email?.toLowerCase().includes(searchLower)
    );
  }, [clients, clientSearchTerm]);

  // Handle duplicate template
  const handleDuplicateTemplate = async (template, e) => {
    e?.stopPropagation();
    const currentName = template.name || template.template_name || 'Unnamed Template';
    const newName = prompt('Enter name for the duplicate template:', `${currentName} (copy)`);
    if (!newName) return;
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const templateData = {
        client_id: "template",
        template_name: newName.trim(),
        date: new Date().toISOString().split('T')[0]
      };
      
      const createResponse = await axios.post(`${API}/workouts`, templateData, { headers });
      const newWorkout = createResponse.data;
      
      const exerciseData = (template.exercises || []).map(ex => ({
        exercise_id: ex.exercise_id,
        exercise_name: ex.exercise_name,
        sets: ex.sets || 4,
        reps: ex.reps || 12,
        weight: ex.weight || 0,
        rest_time: ex.rest_time || 120,
        notes: ex.notes || '',
        is_superset: ex.is_superset || false,
        superset_group: ex.superset_group || 'A'
      }));
      
      await axios.put(`${API}/workouts/${newWorkout.id}/set-exercises`, exerciseData, { headers });
      alert('✅ Template duplicated successfully!');
      fetchAllWorkouts();
    } catch (error) {
      console.error('Error duplicating template:', error);
      alert('Error duplicating template');
    } finally {
      setSaving(false);
    }
  };

  // Category options for exercise filter
  const CATEGORIES = [
    { value: 'all', label: t('all_categories') || 'Todas' },
    { value: 'chest', label: t('chest') || 'Pecho' },
    { value: 'back', label: t('back') || 'Espalda' },
    { value: 'legs', label: t('legs') || 'Piernas' },
    { value: 'shoulders', label: t('shoulders') || 'Hombros' },
    { value: 'arms', label: t('arms') || 'Brazos' },
    { value: 'core', label: t('core') || 'Core' },
    { value: 'cardio', label: t('cardio') || 'Cardio' },
    { value: 'full-body', label: t('full-body') || 'Cuerpo completo' },
    { value: 'stretching', label: t('stretching') || 'Estiramientos' },
    { value: 'plyometrics', label: t('plyometrics') || 'Pliometría' },
    { value: 'neck', label: t('neck') || 'Cuello' }
  ];

  const PRIMARY_MUSCLES = ['Quadriceps', 'Glutes', 'Hamstrings', 'Calves', 'Chest', 'Back', 'Lats', 'Deltoids', 'Biceps', 'Triceps', 'Forearms', 'Abs', 'Obliques', 'Core', 'Trapezius', 'Rhomboids'];

  // Clear all list filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setSortBy('recently_created');
    setUsageFilter('all');
    setTemplateCategoryFilter('all');
  };

  // Template editing functions
  const handleCreateNewTemplate = () => {
    setShowGuidedBuilder(true);
  };
  
  // Guided builder completed — open editor with pre-populated data
  const handleGuidedBuilderComplete = (result) => {
    setShowGuidedBuilder(false);
    setCreatingTemplate(true);
    setEditingWorkout({
      id: null,
      name: result.name,
      exercises: result.exercises,
      status: 'scheduled',
      client_name: 'Template'
    });
  };
  
  // Guided builder "Skip — Start Blank"
  const handleGuidedBuilderSkip = () => {
    setShowGuidedBuilder(false);
    setCreatingTemplate(true);
    setEditingWorkout({
      id: null,
      name: '',
      exercises: [],
      status: 'scheduled',
      client_name: 'Template'
    });
  };

  const handleEditWorkout = (workout, e) => {
    e?.stopPropagation();
    setCreatingTemplate(false);
    setEditingWorkout({
      id: workout.id,
      name: workout.name || workout.template_name || '',
      exercises: workout.exercises || [],
      status: workout.status,
      client_name: workout.client_name
    });
  };

  // Handle template save from unified editor
  const handleSaveTemplateFromEditor = async (booking, workout, exercises, times, mode, templateName) => {
    if (saving) return;
    
    try {
      setSaving(true);
      
      if (!templateName || templateName.trim() === '') {
        throw new Error('Please enter a template name');
      }
      
      if (exercises.length === 0) {
        throw new Error('Please add at least one exercise to the template');
      }
      
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const exerciseData = exercises.map(ex => ({
        exercise_id: ex.exercise_id,
        exercise_name: ex.exercise_name,
        sets: ex.target_sets || ex.sets || 0,
        reps: ex.target_reps || ex.reps || 0,
        weight: ex.target_weight || ex.weight || 0,
        rest_time: ex.rest_time || 0,
        notes: ex.notes || '',
        is_superset: ex.is_superset || false,
        superset_group: ex.superset_group || 'A'
      }));
      
      if (creatingTemplate) {
        // Create new template
        const templateData = {
          client_id: "template",
          template_name: templateName.trim(),
          date: new Date().toISOString().split('T')[0]
        };
        
        const createResponse = await axios.post(`${API}/workouts`, templateData, { headers });
        const newWorkout = createResponse.data;
        
        await axios.put(`${API}/workouts/${newWorkout.id}/set-exercises`, exerciseData, { headers });
      } else {
        // Update existing template
        await axios.put(`${API}/workouts/${editingWorkout.id}`, {
          template_name: templateName.trim()
        }, { headers });
        
        await axios.put(`${API}/workouts/${editingWorkout.id}/set-exercises`, exerciseData, { headers });
      }
      
      setEditingWorkout(null);
      setCreatingTemplate(false);
      fetchAllWorkouts();
      fetchTemplateMetrics();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error saving template:', error);
      throw error; // Re-throw so the editor can show the error
    } finally {
      setSaving(false);
    }
  };

  // Legacy save function - kept for backwards compatibility
  const handleSaveTemplate = async () => {
    if (saving) return;
    
    try {
      setSaving(true);
      
      if (!editingWorkout.name || editingWorkout.name.trim() === '') {
        alert('⚠️ Please enter a template name');
        return;
      }
      
      if (editingWorkout.exercises.length === 0) {
        alert('⚠️ Please add at least one exercise to the template');
        return;
      }
      
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (creatingTemplate) {
        const templateData = {
          client_id: "template",
          template_name: editingWorkout.name.trim(),
          tags: editingWorkout.tags || [],
          focus: editingWorkout.focus || null,
          date: new Date().toISOString().split('T')[0]
        };
        
        const createResponse = await axios.post(`${API}/workouts`, templateData, { headers });
        const newWorkout = createResponse.data;
        
        const exerciseData = editingWorkout.exercises.map(ex => ({
          exercise_id: ex.exercise_id,
          exercise_name: ex.exercise_name,
          sets: ex.sets || ex.target_sets || 4,
          reps: ex.reps || ex.target_reps || 12,
          weight: ex.weight || ex.target_weight || 0,
          rest_time: ex.rest_time || 120,
          notes: ex.notes || '',
          is_superset: ex.is_superset || false,
          superset_group: ex.superset_group || null,
          role: ex.role || null,
          slot_label: ex.slot_label || null,
          image_url: ex.image_url || '',
          animation_webp: ex.animation_webp || '',
        }));
        
        await axios.put(`${API}/workouts/${newWorkout.id}/set-exercises`, exerciseData, { headers });
        alert('Template created successfully!');
      } else {
        const templateName = editingWorkout.name.trim();
        
        // Update template name and metadata
        const updatePayload = { template_name: templateName };
        await axios.put(`${API}/workouts/${editingWorkout.id}`, updatePayload, { headers });
        
        // Also update tags/focus directly in the template collection
        try {
          const token = localStorage.getItem('token');
          await axios.patch(`${API}/workouts/${editingWorkout.id}/template-metadata`, {
            tags: editingWorkout.tags || [],
            focus: editingWorkout.focus || null,
          }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (e) {
          // Metadata update is non-critical — don't block save
          console.warn('Template metadata update skipped:', e.message);
        }
        
        const exerciseData = editingWorkout.exercises.map(ex => ({
          exercise_id: ex.exercise_id,
          exercise_name: ex.exercise_name,
          sets: ex.sets || ex.target_sets || 4,
          reps: ex.reps || ex.target_reps || 12,
          weight: ex.weight || ex.target_weight || 0,
          rest_time: ex.rest_time || 120,
          notes: ex.notes || '',
          is_superset: ex.is_superset || false,
          superset_group: ex.superset_group || null,
          role: ex.role || null,
          slot_label: ex.slot_label || null,
          image_url: ex.image_url || '',
          animation_webp: ex.animation_webp || '',
        }));
        
        await axios.put(`${API}/workouts/${editingWorkout.id}/set-exercises`, exerciseData, { headers });
        alert('Template updated successfully!');
      }
      
      setEditingWorkout(null);
      setCreatingTemplate(false);
      fetchAllWorkouts();
      fetchTemplateMetrics();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('❌ Error saving template: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAddExerciseToWorkout = (exerciseId) => {
    const exercise = exercises.find(e => e.id === exerciseId);
    if (!exercise) return;
    
    const newExercise = {
      exercise_id: exercise.id,
      exercise_name: exercise.name,
      sets: 4,
      reps: 12,
      weight: 0,
      rest_time: 120,
      notes: '',
      image_url: exercise.image_url,
      animation_webp: exercise.animation_webp,
      is_superset: false,
      superset_group: 'A'
    };
    
    setEditingWorkout({
      ...editingWorkout,
      exercises: [...editingWorkout.exercises, newExercise]
    });
  };

  const handleRemoveExerciseFromWorkout = (index) => {
    const updatedExercises = editingWorkout.exercises.filter((_, i) => i !== index);
    setEditingWorkout({ ...editingWorkout, exercises: updatedExercises });
  };

  const handleUpdateExerciseDetail = (index, field, value) => {
    const updatedExercises = [...editingWorkout.exercises];
    updatedExercises[index] = {
      ...updatedExercises[index],
      [field]: field === 'is_superset' ? value : 
               field === 'superset_group' || field === 'notes' ? value :
               field === 'sets' || field === 'reps' || field === 'rest_time' ? parseInt(value) || 0 :
               parseFloat(value) || 0
    };
    setEditingWorkout({ ...editingWorkout, exercises: updatedExercises });
  };

  // Drag & Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedExercise(index);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedExercise(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedExercise !== null && draggedExercise !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedExercise === null || draggedExercise === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    const updatedExercises = [...editingWorkout.exercises];
    const [draggedItem] = updatedExercises.splice(draggedExercise, 1);
    updatedExercises.splice(dropIndex, 0, draggedItem);
    
    setEditingWorkout({ ...editingWorkout, exercises: updatedExercises });
    setDraggedExercise(null);
    setDragOverIndex(null);
  };

  const handleMoveExercise = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= editingWorkout.exercises.length) return;
    
    const updatedExercises = [...editingWorkout.exercises];
    [updatedExercises[index], updatedExercises[newIndex]] = 
    [updatedExercises[newIndex], updatedExercises[index]];
    
    setEditingWorkout({ ...editingWorkout, exercises: updatedExercises });
  };

  // Toggle template expansion
  const toggleExpand = (templateId) => {
    setExpandedTemplateId(expandedTemplateId === templateId ? null : templateId);
  };

  // Get exercise info from library
  const getExerciseInfo = (exerciseId) => {
    return exercises.find(e => e.id === exerciseId) || {};
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto mb-4"></div>
        <p className="text-blue-300">Loading templates...</p>
      </div>
    );
  }

  const hasActiveFilters = searchTerm || templateCategoryFilter !== 'all' || usageFilter !== 'all' || sortBy !== 'recently_created';

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Dumbbell className="w-5 h-5" />
            Templates
          </h2>
          <p className="text-blue-400 text-sm mt-0.5">{workouts.length} templates total</p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => { fetchAllWorkouts(); fetchTemplateMetrics(); }} 
            className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 transition"
            title="Refresh"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button 
            onClick={handleCreateNewTemplate} 
            className="btn-primary flex items-center gap-2" 
            data-testid="create-template-btn"
          >
            <span>+</span>
            Create Template
          </button>
        </div>
      </div>

      {/* Filter Bar - Redesigned */}
      <div className="glass-card p-4 mb-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search templates..."
              className="input-field pl-10 pr-8 text-sm w-full"
              data-testid="template-search"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')} 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Category Filter - PRIMARY */}
          <div className="flex-1 max-w-xs">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
              <select
                value={templateCategoryFilter}
                onChange={(e) => setTemplateCategoryFilter(e.target.value)}
                className="input-field pl-10 text-sm w-full appearance-none"
                data-testid="category-filter"
              >
                <option value="all">All Categories</option>
                {ALL_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="input-field text-sm w-full lg:w-44"
            data-testid="template-sort"
          >
            <option value="recently_created">Recently Created</option>
            <option value="recently_used">Recently Used</option>
            <option value="most_used">Most Used</option>
            <option value="name_asc">Name A-Z</option>
          </select>
          
          {/* Usage Filter */}
          <select
            value={usageFilter}
            onChange={(e) => setUsageFilter(e.target.value)}
            className="input-field text-sm w-full lg:w-40"
            data-testid="template-usage-filter"
          >
            <option value="all">All Usage</option>
            <option value="unused">Unused (0)</option>
            <option value="frequently_used">Frequent (5+)</option>
          </select>
        </div>
        
        {/* Active filters & results */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-blue-400/20">
          <p className="text-blue-300 text-sm">
            Showing <span className="text-white font-medium">{filteredTemplates.length}</span> of {workouts.length} templates
          </p>
          {hasActiveFilters && (
            <button 
              onClick={clearAllFilters} 
              className="text-blue-400 hover:text-white text-sm flex items-center gap-1 transition"
            >
              <X className="w-3 h-3" />
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Templates List - Compact Cards */}
      <div className="space-y-2">
        {paginatedTemplates.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <Dumbbell className="w-12 h-12 mx-auto mb-3 text-blue-400/50" />
            <h3 className="text-lg font-semibold text-blue-200 mb-2">No Templates Found</h3>
            <p className="text-blue-300 text-sm">
              {workouts.length === 0 
                ? 'Create your first template to get started.'
                : 'Try adjusting your filters.'}
            </p>
          </div>
        ) : (
          paginatedTemplates.map((template) => {
            const metrics = templateMetrics[template.id] || {};
            const usageCount = metrics.count || 0;
            const isExpanded = expandedTemplateId === template.id;
            const templateCategory = getTemplateCategory(template.exercises, exercises);
            const categoryColor = getCategoryColor(templateCategory);
            const exerciseCount = template.exercises?.length || 0;
            
            return (
              <div 
                key={template.id} 
                className={`glass-card overflow-hidden transition-all duration-200 ${isExpanded ? 'ring-1 ring-blue-400/50' : ''}`}
                data-testid="template-card"
              >
                {/* Compact Header - Always visible */}
                <div 
                  className="p-3 cursor-pointer hover:bg-blue-500/5 transition-colors"
                  onClick={() => toggleExpand(template.id)}
                >
                  <div className="flex items-center gap-3">
                    {/* Expand Icon */}
                    <div className="text-blue-400 flex-shrink-0">
                      {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </div>
                    
                    {/* Template Name & Primary Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-white truncate">
                          {template.name || template.template_name || 'Unnamed Template'}
                        </h3>
                        
                        {/* Category Tag (PRIMARY) */}
                        {templateCategory && (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${categoryColor.bg} ${categoryColor.text} border ${categoryColor.border}`}>
                            {categoryColor.label}
                          </span>
                        )}
                        
                        {/* Focus + Tags */}
                        {template.focus && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/25 text-blue-200">
                            {template.focus}
                          </span>
                        )}
                        {(template.tags || []).filter(t => t !== template.focus).slice(0, 3).map((tag, i) => (
                          <span key={i} className="px-1.5 py-0.5 rounded text-[10px] bg-gray-600/30 text-gray-400">
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      {/* Exercise Preview - First 3 + N more */}
                      <div className="flex items-center gap-2 mt-1 text-sm">
                        <span className="text-blue-300">{exerciseCount} exercises</span>
                        <span className="text-blue-500">·</span>
                        <span className="text-amber-300" data-testid="template-est-duration" title="Estimated duration">
                          ~{formatDuration(estimateWorkoutSeconds(template.exercises || []))}
                        </span>
                        <span className="text-blue-500">·</span>
                        <span className="text-blue-400 truncate">
                          {template.exercises?.slice(0, 3).map(ex => ex.exercise_name).join(', ')}
                          {exerciseCount > 3 && <span className="text-blue-500"> +{exerciseCount - 3} more</span>}
                        </span>
                      </div>
                    </div>
                    
                    {/* Metrics */}
                    <div className="flex items-center gap-4 text-sm flex-shrink-0">
                      <div className="text-center hidden sm:block">
                        <div className={`font-medium ${usageCount > 0 ? 'text-purple-300' : 'text-gray-500'}`}>
                          {usageCount}
                        </div>
                        <div className="text-blue-500 text-xs">uses</div>
                      </div>
                      <div className="text-center hidden md:block">
                        <div className="text-blue-300">{formatDate(template.created_at)}</div>
                        <div className="text-blue-500 text-xs">created</div>
                      </div>
                    </div>
                    
                    {/* Quick Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setAssignFlexibleModal(template); }}
                        className="p-2 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 transition"
                        title="Assign template (booking or free routine)"
                        data-testid="assign-template-btn"
                      >
                        <UserPlus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setBulkAssignModal(template); }}
                        className="p-2 rounded bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 transition"
                        title="Asignar a varios clientes"
                        data-testid="bulk-assign-btn"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleEditWorkout(template, e)}
                        className="p-2 rounded bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 transition"
                        title="Edit"
                        data-testid="edit-template-btn"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={(e) => handleDuplicateTemplate(template, e)}
                        className="p-2 rounded bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 transition"
                        title="Duplicate"
                        data-testid="duplicate-template-btn"
                      >
                        📋
                      </button>
                      <button
                        onClick={(e) => handleDeleteWorkout(template.id, e)}
                        className="p-2 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 transition"
                        title="Delete"
                        data-testid="delete-template-btn"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Expanded Content - Full Exercise List */}
                {isExpanded && (
                  <div className="border-t border-blue-400/20 bg-blue-950/30">
                    {/* Exercise List */}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-blue-200 font-medium text-sm">Exercise List</h4>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => fetchUsageDetails(template.id)}
                            className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-300 hover:bg-green-500/30 transition flex items-center gap-1"
                            data-testid="usage-details-btn"
                          >
                            <Users className="w-3 h-3" />
                            Usage by Client
                          </button>
                          <button
                            onClick={(e) => handleDuplicateTemplate(template, e)}
                            className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition"
                            data-testid="duplicate-template-btn"
                          >
                            📋 Duplicate
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        {(template.exercises || []).map((ex, idx) => {
                          const exInfo = getExerciseInfo(ex.exercise_id);
                          const exMuscles = exInfo.primary_muscle_groups || exInfo.muscle_groups || [];
                          const primaryExMuscle = exMuscles[0];
                          const exMuscleColor = getMuscleColor(primaryExMuscle);
                          
                          return (
                            <div 
                              key={idx}
                              className={`flex items-center gap-3 p-2 rounded-lg ${
                                ex.is_superset ? 'bg-orange-900/20 border-l-2 border-orange-400' : 'bg-blue-900/20'
                              }`}
                            >
                              {/* Order Number + Role Dot */}
                              <span className="text-blue-500 font-mono text-sm w-6 text-center flex-shrink-0 flex items-center gap-1">
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                  ex.role === 'main' ? 'bg-white' : ex.role === 'secondary' ? 'bg-blue-400' : ex.role === 'accessory' ? 'bg-gray-400' : ex.role === 'isolation' ? 'bg-gray-600' : 'bg-gray-700'
                                }`} />
                                {idx + 1}
                              </span>
                              
                              {/* Thumbnail */}
                              <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-blue-800/50">
                                {exInfo.image_url ? (
                                  <img 
                                    src={exInfo.image_url} 
                                    alt={ex.exercise_name} 
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-lg">🏋️</div>
                                )}
                              </div>
                              
                              {/* Exercise Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-white font-medium truncate">{ex.exercise_name}</span>
                                  {ex.role && (
                                    <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0 ${
                                      ex.role === 'main' ? 'bg-white/15 text-white' : ex.role === 'secondary' ? 'bg-blue-500/20 text-blue-300' : ex.role === 'accessory' ? 'bg-gray-500/15 text-gray-400' : 'bg-gray-500/10 text-gray-500'
                                    }`}>{ex.role}</span>
                                  )}
                                  {ex.is_superset && (
                                    <span className="text-orange-400 text-xs">SS-{ex.superset_group}</span>
                                  )}
                                </div>
                                <div className="text-blue-400 text-xs">
                                  {ex.sets}×{ex.reps} · {ex.rest_time}s rest
                                  {ex.weight > 0 && ` · ${ex.weight}kg`}
                                </div>
                              </div>
                              
                              {/* Muscle Tag */}
                              {primaryExMuscle && (
                                <span className={`px-2 py-0.5 rounded text-xs ${exMuscleColor.bg} ${exMuscleColor.text} flex-shrink-0 hidden sm:inline`}>
                                  {primaryExMuscle}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Additional Info */}
                      {metrics.lastUsed && (
                        <div className="mt-3 pt-3 border-t border-blue-400/10 text-xs text-blue-400">
                          Last used: {formatDate(metrics.lastUsed)} · {metrics.distinctClients || 0} clients
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 rounded bg-blue-500/20 text-blue-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-500/30 transition"
          >
            ← Prev
          </button>
          <span className="text-blue-300 px-4">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 rounded bg-blue-500/20 text-blue-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-500/30 transition"
          >
            Next →
          </button>
        </div>
      )}

      {/* Usage Details Modal */}
      {usageDetailsModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/70 z-[9999] overflow-y-auto" onClick={() => setUsageDetailsModal(null)}>
          <div className="min-h-screen flex items-start sm:items-center justify-center p-4">
            <div className="bg-blue-900/95 rounded-xl max-w-2xl w-full my-4 sm:my-8 max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-blue-400/30 flex justify-between items-center">
              <h3 className="text-white font-bold text-lg">
                👥 Usage: {usageDetailsModal.template_name}
              </h3>
              <button onClick={() => setUsageDetailsModal(null)} className="text-blue-300 hover:text-white text-xl">✕</button>
            </div>
            <div className="p-4">
              <div className="flex gap-4 mb-4 text-sm">
                <span className="bg-purple-500/20 text-purple-200 px-3 py-1 rounded">
                  🔁 {usageDetailsModal.total_executions} total uses
                </span>
                <span className="bg-green-500/20 text-green-200 px-3 py-1 rounded">
                  👤 {usageDetailsModal.distinct_clients} clients
                </span>
              </div>
              
              {usageDetailsModal.client_breakdown?.length > 0 ? (
                <div className="space-y-4 max-h-[55vh] overflow-y-auto">
                  {usageDetailsModal.client_breakdown.map((client, idx) => (
                    <div key={idx} className="bg-blue-900/30 rounded-lg overflow-hidden">
                      <div className="flex justify-between items-center p-3 border-b border-blue-400/20">
                        <span className="text-white font-medium">{client.client_name}</span>
                        <span className="text-purple-300 text-sm">{client.count}× completed</span>
                      </div>
                      
                      {client.executions && client.executions.length > 0 && (
                        <div className="p-2 space-y-2">
                          {client.executions.map((exec, execIdx) => (
                            <div 
                              key={execIdx} 
                              className="flex items-center justify-between bg-blue-800/30 rounded px-3 py-2"
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-blue-400 text-xs font-mono">#{execIdx + 1}</span>
                                <div>
                                  <p className="text-white text-sm">{formatDate(exec.date)}</p>
                                  {exec.total_volume > 0 && (
                                    <p className="text-blue-300 text-xs">
                                      💪 {exec.total_volume.toLocaleString()} kg volume
                                    </p>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const response = await axios.get(`${API}/workouts/${exec.workout_id}`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    setViewingExecutedWorkout({
                                      workout: response.data,
                                      clientName: client.client_name
                                    });
                                  } catch (error) {
                                    console.error('Error fetching workout:', error);
                                    alert('Error loading workout details');
                                  }
                                }}
                                className="btn-secondary text-xs px-2 py-1 flex items-center gap-1"
                                data-testid="view-execution-btn"
                              >
                                👁️ View
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-blue-300 text-center py-4">No usage data yet</p>
              )}
            </div>
            <div className="p-4 border-t border-blue-400/30">
              <button onClick={() => setUsageDetailsModal(null)} className="btn-secondary w-full">Close</button>
            </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* View Executed Workout Modal */}
      {viewingExecutedWorkout && (
        <WorkoutViewModal
          booking={null}
          workout={{
            ...viewingExecutedWorkout.workout,
            client_name: viewingExecutedWorkout.clientName
          }}
          onClose={() => setViewingExecutedWorkout(null)}
          t={t}
        />
      )}

      {/* Legacy Assign to Booking Modal - kept for backwards compatibility */}
      {assignModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/70 z-[9999] overflow-y-auto" onClick={() => setAssignModal(null)}>
          <div className="min-h-screen flex items-start sm:items-center justify-center p-4">
            <div className="bg-blue-900/95 rounded-xl max-w-lg w-full my-4 sm:my-8 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 border-b border-blue-400/30 flex justify-between items-center">
                <h3 className="text-white font-bold text-lg">
                  📎 Assign: {assignModal.name || assignModal.template_name || 'Template'}
                </h3>
                <button onClick={() => setAssignModal(null)} className="text-blue-300 hover:text-white text-xl">✕</button>
              </div>
              <div className="p-4">
                <p className="text-blue-300 mb-4">Select a booking to assign this template:</p>
                
                {loadingBookings ? (
                  <p className="text-center py-4 text-blue-300">Loading bookings...</p>
                ) : futureBookings.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-blue-300 mb-2">No eligible bookings available</p>
                    <p className="text-blue-400 text-xs">
                      Eligible bookings must be: today or future, status RESERVED/ASSIGNED
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                    {futureBookings.map((booking) => (
                      <div 
                        key={booking.id} 
                        onClick={() => handleAssignTemplate(booking.id)}
                        className="flex justify-between items-center p-3 bg-blue-900/30 rounded-lg cursor-pointer hover:bg-blue-800/40 transition"
                        data-testid={`assign-booking-${booking.id}`}
                      >
                        <div>
                          <span className="text-white font-medium">{booking.client_name}</span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-blue-300 text-sm">
                              {booking.date} {booking.start_time}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {booking.has_workout ? (
                            <span className="text-yellow-400 text-xs px-2 py-0.5 bg-yellow-500/20 rounded">
                              Has workout ({booking.workout_exercises_count} ex)
                            </span>
                          ) : (
                            <span className="text-green-400 text-xs px-2 py-0.5 bg-green-500/20 rounded">
                              Ready
                            </span>
                          )}
                          <span className="text-blue-400">→</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-blue-400/30">
                <button onClick={() => setAssignModal(null)} className="btn-secondary w-full">Cancel</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create Session for Client Modal */}
      {createSessionModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/70 z-[9999] overflow-y-auto" onClick={() => setCreateSessionModal(null)}>
          <div className="min-h-screen flex items-start sm:items-center justify-center p-4">
            <div className="bg-slate-800 rounded-xl max-w-lg w-full my-4 sm:my-8 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 border-b border-slate-700 flex justify-between items-center">
                <div>
                  <h3 className="text-white font-bold text-lg flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-purple-400" />
                    Create Session
                  </h3>
                  <p className="text-blue-300 text-sm mt-1">
                    {createSessionModal.name || createSessionModal.template_name || 'Template'}
                  </p>
                  <p className="text-amber-300 text-xs mt-1 flex items-center gap-1" data-testid="create-session-est-duration">
                    <Clock className="w-3 h-3" /> Duración estimada: ~{formatDuration(estimateWorkoutSeconds(createSessionModal.exercises || []))}
                  </p>
                </div>
                <button onClick={() => setCreateSessionModal(null)} className="text-blue-300 hover:text-white text-xl">✕</button>
              </div>
              <div className="p-4">
                <p className="text-blue-200 mb-4">Select a client to create a session with this template:</p>
                
                {/* Client Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={clientSearchTerm}
                    onChange={(e) => setClientSearchTerm(e.target.value)}
                    placeholder="Search clients..."
                    className="input-field w-full pl-10"
                    data-testid="client-search-input"
                  />
                </div>
                
                {loadingClients ? (
                  <p className="text-center py-4 text-blue-300">Loading clients...</p>
                ) : filteredClients.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-slate-400 mb-2">No clients found</p>
                    {clientSearchTerm && (
                      <p className="text-blue-400 text-xs">Try a different search term</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                    {filteredClients.map((client) => (
                      <div 
                        key={client.id} 
                        onClick={() => handleCreateSessionForClient(client.id)}
                        className="flex justify-between items-center p-3 bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700 transition group"
                        data-testid={`select-client-${client.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                            {client.full_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <span className="text-white font-medium">{client.full_name}</span>
                            <div className="text-blue-300 text-sm">
                              {client.email}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            client.status === 'active' ? 'bg-green-500/20 text-green-300' : 
                            client.status === 'onboarding' ? 'bg-yellow-500/20 text-yellow-300' :
                            'bg-slate-500/20 text-slate-300'
                          }`}>
                            {client.status || 'Active'}
                          </span>
                          <span className="text-purple-400 opacity-0 group-hover:opacity-100 transition">→</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-slate-700">
                <p className="text-blue-400/70 text-xs mb-3 text-center">
                  You&apos;ll be redirected to the client&apos;s planner to create the session
                </p>
                <button onClick={() => setCreateSessionModal(null)} className="btn-secondary w-full">Cancel</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}


      {/* Guided Template Builder */}
      {showGuidedBuilder && (
        <GuidedTemplateBuilder
          exercises={exercises}
          onComplete={handleGuidedBuilderComplete}
          onSkip={handleGuidedBuilderSkip}
          onCancel={() => setShowGuidedBuilder(false)}
        />
      )}

      {/* Edit/Create Template - Using Unified Full Screen Editor */}
      {editingWorkout && (
        <FullScreenWorkoutEditor
          booking={null}
          workout={{
            id: editingWorkout.id,
            exercises: editingWorkout.exercises,
            template_name: editingWorkout.name || '',
            trainer_notes: editingWorkout.name || ''
          }}
          mode="template"
          exercises={exercises}
          templates={[]}
          onSave={handleSaveTemplateFromEditor}
          onClose={() => { setEditingWorkout(null); setCreatingTemplate(false); }}
          templateName={editingWorkout.name || ''}
          t={t}
        />
      )}

      {/* Phase 2: Flexible workout assignment modal */}
      {assignFlexibleModal && (
        <AssignFlexibleModal
          template={assignFlexibleModal}
          onClose={() => setAssignFlexibleModal(null)}
          onAssigned={() => {
            // Show a tiny success toast — keep it lightweight
            try { fetchAllWorkouts(); } catch (e) { console.warn('template refresh failed:', e); }
          }}
        />
      )}

      {/* P0-2: Bulk assignment modal */}
      {bulkAssignModal && (
        <BulkAssignTemplateModal
          template={bulkAssignModal}
          onClose={() => setBulkAssignModal(null)}
          onCompleted={() => {
            try { fetchAllWorkouts(); } catch (e) { console.warn('template refresh failed:', e); }
          }}
        />
      )}
    </div>
  );
};

export default WorkoutTemplateLibrary;
